import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { fmtPrice, fmtUsd } from '../utils/fmtPrice';
import { positionStatusLabel, positionKey } from '../utils/tradeActionUi';
import { isSessionAuthError, SESSION_AUTH_MESSAGE, truncateStatusMessage } from '../utils/apiAuth';
import './OpenPositionsSection.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const FILLS_VISIBLE_ROWS = 10;
/** One compact lot row (oBot, no action buttons) — tune with CSS --lot-row-block-height */
const LOT_ROW_BLOCK_PX = 78;
const LOT_ROW_GAP_PX = 12;

function lotListMaxHeight(maxVisibleLots) {
  if (maxVisibleLots == null || maxVisibleLots <= 0) return undefined;
  const h = maxVisibleLots * LOT_ROW_BLOCK_PX + Math.max(0, maxVisibleLots - 1) * LOT_ROW_GAP_PX;
  return `${h}px`;
}

function fmtFillTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function fmtQty(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1) return n.toFixed(8).replace(/\.?0+$/, '') || n.toFixed(8);
  return n.toFixed(8);
}

function lotMarginUsd(p) {
  return p.marginUsd ?? p.sizeUsd ?? 0;
}

function groupBySymbol(rows, key = 'symbol') {
  const map = {};
  for (const row of rows || []) {
    const sym = row[key];
    if (!sym) continue;
    if (!map[sym]) map[sym] = [];
    map[sym].push(row);
  }
  return map;
}

function PositionRow({ p, isSelected, busy, onTradeLot, onCloseLot, onSelect }) {
  const margin = lotMarginUsd(p);
  const lev = p.leverage || 1;
  const statusLabel = p.positionAction || positionStatusLabel(p.side);
  const selectable = !!onSelect && !onTradeLot && !onCloseLot;

  return (
    <li
      className={`open-position-row ${p.side} ${isSelected ? 'selected' : ''}${selectable ? ' open-position-row-selectable' : ''}`}
      onClick={selectable ? () => onSelect(p) : undefined}
      onKeyDown={
        selectable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(p);
              }
            }
          : undefined
      }
      tabIndex={selectable ? 0 : undefined}
      role={selectable ? 'button' : undefined}
    >
      <div className="open-position-row-main">
        <div className="open-position-row-top">
          <strong className="position-symbol">{p.symbol}</strong>
          {p.lotCount > 1 && (
            <span className="position-lot-count-pill" title="FIFO lots merged into one row">
              {p.lotCount} lots
            </span>
          )}
          <span className={`position-side-pill ${p.side}`}>{statusLabel}</span>
          <span className={`position-mode-pill ${p.isPaper ? 'paper' : 'live'}`}>
            {p.isPaper ? 'Paper' : 'Live'}
          </span>
        </div>
        <div className="open-position-row-stats">
          <span>
            Size <strong>{fmtUsd(margin)}</strong>
            {p.lotCount > 1 && margin > 0 && (
              <span className="position-avg-margin" title="Average margin per FIFO lot">
                {' '}
                (~{fmtUsd(margin / p.lotCount)}/lot)
              </span>
            )}
          </span>
          <span>
            Leverage <strong>{lev}x</strong>
          </span>
          <span>
            Qty <strong>{fmtQty(p.sizeContracts)}</strong>
          </span>
          <span>
            Price <strong>{fmtPrice(p.entryPrice)}</strong>
          </span>
          {p.markPrice != null && (
            <span>
              Mark <strong>{fmtPrice(p.markPrice)}</strong>
            </span>
          )}
          {p.unrealizedPnl != null && (
            <span className={p.unrealizedPnl >= 0 ? 'pnl-pos' : 'pnl-neg'}>
              uPnL <strong>{fmtUsd(p.unrealizedPnl)}</strong>
            </span>
          )}
        </div>
      </div>
      {(onTradeLot || onCloseLot) && (
        <div className="open-position-row-actions">
          {onTradeLot && (
            <button
              type="button"
              className="open-position-trade-btn"
              disabled={busy}
              onClick={() => onTradeLot(p)}
              title={
                p.side === 'long'
                  ? 'Partial sell — reduce this long lot'
                  : 'Partial buy — reduce this short lot'
              }
            >
              Trade
            </button>
          )}
          {onCloseLot && (
            <button
              type="button"
              className="open-position-close-btn"
              disabled={busy}
              onClick={() => onCloseLot(p)}
              title={
                p.side === 'long'
                  ? 'Sell entire lot (full close)'
                  : 'Buy entire lot (full close)'
              }
            >
              {busy ? '…' : 'Close'}
            </button>
          )}
        </div>
      )}
    </li>
  );
}

/** Scroll wrapper — height capped to exactly maxVisibleLots rows */
function lotListScrollStyle(maxVisibleLots) {
  const maxHeight = lotListMaxHeight(maxVisibleLots);
  if (!maxHeight) return undefined;
  return { maxHeight, overflowY: 'auto' };
}

function CompactLotList({
  positions,
  maxVisibleLots,
  isRowSelected,
  busyKey,
  onSelectPosition,
  onTradeLot,
  onCloseLot,
  showLotActions,
  handleCloseLot,
}) {
  const scrollStyle = lotListScrollStyle(maxVisibleLots);

  return (
    <>
      {maxVisibleLots != null && positions.length > maxVisibleLots && (
        <p className="open-positions-fills-meta">
          {positions.length} position{positions.length === 1 ? '' : 's'} — showing {maxVisibleLots} at a
          time, scroll for more
        </p>
      )}
      <div
        className="open-positions-lot-list-scroll open-positions-lot-list-scroll-capped"
        style={scrollStyle}
      >
        <ul className="open-positions-lot-list">
          {positions.map((p) => {
            const key = positionKey(p);
            const isSelected = isRowSelected(p);
            const rowBusy = busyKey === key;
            return (
              <PositionRow
                key={key}
                p={p}
                isSelected={isSelected}
                busy={rowBusy}
                onSelect={showLotActions ? undefined : onSelectPosition}
                onTradeLot={showLotActions ? onTradeLot : undefined}
                onCloseLot={showLotActions ? handleCloseLot : undefined}
              />
            );
          })}
        </ul>
      </div>
    </>
  );
}

function AssetFillsTable({ fills, isRowSelected, onSelectPosition }) {
  const scrollStyle = { maxHeight: `${38 + FILLS_VISIBLE_ROWS * 38}px` };

  return (
    <div className="open-positions-fills-wrap open-positions-fills-scroll" style={scrollStyle}>
      <table className="open-positions-fills-table">
        <thead>
          <tr>
            <th>Date / time</th>
            <th>Side</th>
            <th>Size</th>
            <th>Entry</th>
            <th>Mark</th>
            <th>Order</th>
            <th>Signal</th>
          </tr>
        </thead>
        <tbody>
          {fills.map((p) => {
            const key = positionKey(p);
            const isSelected = isRowSelected(p);
            const statusLabel = p.positionAction || positionStatusLabel(p.side);
            return (
              <tr
                key={`fill-${p.orderId || key}`}
                className={`open-positions-fill-row ${p.side} ${isSelected ? 'selected' : ''}`}
                onClick={() => onSelectPosition?.(p)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectPosition?.(p);
                  }
                }}
                tabIndex={0}
                role="button"
              >
                <td className="fill-time">{fmtFillTime(p.createdAt)}</td>
                <td>
                  <span className={`position-side-pill ${p.side}`}>{statusLabel}</span>
                  {p.isPaper && <span className="position-paper-pill">PAPER</span>}
                </td>
                <td>{fmtUsd(lotMarginUsd(p))}</td>
                <td>{fmtPrice(p.entryPrice)}</td>
                <td>{fmtPrice(p.markPrice)}</td>
                <td className="fill-id">{p.orderId ? `#${p.orderId}` : '—'}</td>
                <td className="fill-id">{p.signalId ? `#${p.signalId}` : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function OpenPositionsSection({
  onSelectPosition,
  onTradeLot,
  onCloseLot,
  selectedPositionKey,
  selectedSymbol,
  onStatusMessage,
  onPositionsChanged,
  refreshKey = 0,
  scope = 'all',
  title = 'Open positions',
  maxVisibleLots = null,
  apiPaths = null,
}) {
  const isRowSelected = (p) => {
    if (selectedPositionKey) return selectedPositionKey === positionKey(p);
    if (selectedSymbol) return selectedSymbol === p.symbol;
    return false;
  };

  const [positions, setPositions] = useState([]);
  const [fills, setFills] = useState([]);
  const [walletAddress, setWalletAddress] = useState(null);
  const [paperTestBalanceUsd, setPaperTestBalanceUsd] = useState(null);
  const [paperMode, setPaperMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [assetTabs, setAssetTabs] = useState({});
  const [sessionExpired, setSessionExpired] = useState(false);
  const [bookWarning, setBookWarning] = useState(null);
  const [reconcileBusy, setReconcileBusy] = useState(false);

  const manualScope = scope === 'manual';
  const obotScope = scope === 'obot';
  const omniScope = scope === 'omni';
  const showLotActions = (manualScope || obotScope || omniScope) && (onTradeLot || onCloseLot);

  const load = useCallback(async () => {
    if (sessionExpired) return;
    setError(null);
    try {
      const positionsUrl = apiPaths?.positions || `${API_BASE}/multi-asset/positions`;
      const params = manualScope || obotScope ? { scope } : {};
      const { data } = await axios.get(positionsUrl, {
        headers: authHeaders(),
        params,
      });
      setPositions(data.positions || []);
      setFills(data.fills || []);
      setWalletAddress(data.walletAddress || null);
      setPaperMode(!!data.paperMode);
      setPaperTestBalanceUsd(
        data.paperTestBalanceUsd != null ? Number(data.paperTestBalanceUsd) : null
      );
      setBookWarning(
        data.bookExceedsPaperBalance || data.spamLikely
          ? {
              exceedsPaper: !!data.bookExceedsPaperBalance,
              spamLikely: !!data.spamLikely,
              totalMarginUsd: data.totalMarginUsd,
              maxAggLotCount: data.maxAggLotCount,
              totalLotCount: data.totalLotCount,
            }
          : null
      );
    } catch (e) {
      if (isSessionAuthError(e)) {
        setSessionExpired(true);
        setError(SESSION_AUTH_MESSAGE);
      } else {
        setError(e.response?.data?.error || e.message);
      }
      setPositions([]);
      setFills([]);
    } finally {
      setLoading(false);
    }
  }, [manualScope, obotScope, omniScope, scope, apiPaths, sessionExpired]);

  useEffect(() => {
    setLoading(true);
    load();
    if (sessionExpired) return undefined;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load, refreshKey, sessionExpired]);

  const netBySymbol = useMemo(() => groupBySymbol(positions), [positions]);
  const fillsBySymbol = useMemo(() => groupBySymbol(fills), [fills]);
  const symbols = useMemo(
    () => Object.keys(netBySymbol).sort((a, b) => a.localeCompare(b)),
    [netBySymbol]
  );

  const portfolioTotalUsd = useMemo(
    () => positions.reduce((sum, p) => sum + (Number(lotMarginUsd(p)) || 0), 0),
    [positions]
  );

  const portfolioUpnl = useMemo(() => {
    const withPnl = positions.filter((p) => p.unrealizedPnl != null);
    if (!withPnl.length) return null;
    return withPnl.reduce((sum, p) => sum + (Number(p.unrealizedPnl) || 0), 0);
  }, [positions]);

  const setAssetTab = (symbol, tab) => {
    setAssetTabs((prev) => ({ ...prev, [symbol]: tab }));
  };

  const handleCloseLot = async (p) => {
    const margin = lotMarginUsd(p);
    const lev = p.leverage || 1;
    if (
      !window.confirm(
        `Close entire ${p.side} ${p.symbol}? (Margin ${fmtUsd(margin)} · ${lev}x · ${fmtQty(p.sizeContracts)} qty)`
      )
    ) {
      return;
    }
    const key = positionKey(p);
    setBusyKey(key);
    setBusy(true);
    try {
      await onCloseLot?.(p);
      onPositionsChanged?.();
      await load();
    } catch (e) {
      onStatusMessage?.(e.response?.data?.error || e.message, true);
    } finally {
      setBusyKey(null);
      setBusy(false);
    }
  };

  const reconcileSpamLots = async () => {
    if (
      !window.confirm(
        'Collapse duplicate algorithm lots? This closes the full spam stack per symbol (e.g. 1,300 GOLD adds) and re-opens a single lot at macro deploy size (~$5k).'
      )
    ) {
      return;
    }
    setReconcileBusy(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/multi-asset/reconcile-spam-lots`,
        { scope: 'obot', keepOne: true },
        { headers: authHeaders() }
      );
      const closed = data.closed?.length || 0;
      const reopened = data.reopened?.length || 0;
      onStatusMessage?.(
        closed
          ? `Collapsed ${closed} symbol(s); reopened ${reopened} single lot(s). Refresh positions.`
          : data.skipped?.length
            ? 'No spam stacks collapsed — see server response.'
            : 'Done.'
      );
      onPositionsChanged?.();
      await load();
    } catch (e) {
      onStatusMessage?.(e.response?.data?.error || e.message, true);
    } finally {
      setReconcileBusy(false);
    }
  };

  const liquidateAll = async () => {
    const confirmText = manualScope
      ? 'Close all open manual positions? Paper mode fills immediately after you confirm.'
      : obotScope
        ? 'Close all oBot positions? Paper fills immediately; live uses your HL wallet via oBot signing.'
        : omniScope
          ? 'Close all Omni MAP master positions? Paper fills immediately; live uses server HL wallet.'
          : 'Close all open positions?';
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    try {
      const liquidateUrl = apiPaths?.liquidateAll || `${API_BASE}/multi-asset/liquidate-all`;
      const body =
        scope === 'manual'
          ? { scope: 'manual' }
          : obotScope
            ? { scope: 'obot' }
            : omniScope
              ? {}
              : {};
      const { data } = await axios.post(liquidateUrl, body, { headers: authHeaders() });
      onStatusMessage?.(truncateStatusMessage(data.message || `Queued ${data.count} close(s).`));
      onPositionsChanged?.();
      await load();
    } catch (e) {
      onStatusMessage?.(e.response?.data?.error || e.message, true);
    } finally {
      setBusy(false);
    }
  };

  const hintText = manualScope
    ? 'Each asset has Summary (open lots) and Trades (same lots in table form). Use Trade for a partial close or Close for the full lot.'
    : obotScope
      ? 'Your oBot / automated MAP positions (not manual trades). Click a lot to trade or close partially.'
      : omniScope
        ? 'Omni MAP master account — admin override when AI signals are delayed. Partial trade or close per lot.'
        : 'Each asset has Summary (net position) and Trades (every fill still open). Click to close individually.';

  const emptyMessage = manualScope
    ? 'No manual positions yet. Queue a trade above.'
    : obotScope
      ? 'No oBot positions yet. Enable oBot or queue a trade below.'
      : omniScope
        ? 'No Omni MAP positions on the master account.'
        : 'No open positions. Queue a trade below or enable oBot.';

  const useCompactLotList = maxVisibleLots != null && maxVisibleLots > 0;

  return (
    <section className="open-positions-section">
      <div className="open-positions-header">
        <div>
          <h2>{title}</h2>
          <p className="open-positions-hint">
            {hintText}{' '}
            {obotScope && paperMode && paperTestBalanceUsd != null ? (
              <span>Paper test balance: {fmtUsd(paperTestBalanceUsd)} (from wallet settings).</span>
            ) : null}{' '}
            {walletAddress ? (
              <span className="wallet-addr">
                HL: {walletAddress.slice(0, 6)}…{walletAddress.slice(-4)}
              </span>
            ) : (
              <span>Link wallet in settings for live HL positions; paper fills show from your account.</span>
            )}
          </p>
        </div>
        <div className="open-positions-actions">
          <button type="button" className="secondary-btn" disabled={busy} onClick={load}>
            Refresh
          </button>
          <button
            type="button"
            className="danger-btn"
            disabled={busy || !positions.length}
            onClick={liquidateAll}
          >
            Liquidate all
          </button>
        </div>
      </div>

      {error && <p className="open-positions-error">{error}</p>}
      {bookWarning && obotScope && (
        <div className="open-positions-spam-warning">
          <p>
            <strong>Paper book mismatch.</strong> Total margin {fmtUsd(bookWarning.totalMarginUsd ?? portfolioTotalUsd)}{' '}
            {paperTestBalanceUsd != null ? (
              <>exceeds your paper test balance {fmtUsd(paperTestBalanceUsd)}.</>
            ) : (
              <>is inflated by duplicate algorithm fills.</>
            )}
            {bookWarning.maxAggLotCount > 1 ? (
              <> One row can still represent <strong>{bookWarning.maxAggLotCount}</strong> merged FIFO lots — margin is the sum of every fill still on the ledger.</>
            ) : null}
          </p>
          <button
            type="button"
            className="secondary-btn"
            disabled={reconcileBusy || busy}
            onClick={reconcileSpamLots}
          >
            {reconcileBusy ? 'Collapsing…' : 'Collapse duplicate algo lots'}
          </button>
        </div>
      )}
      {loading && <p className="open-positions-loading">Loading positions…</p>}

      {!loading && !positions.length && !error && (
        <p className="open-positions-empty">{emptyMessage}</p>
      )}

      {!loading && positions.length > 0 && (
        <>
          <div className="open-positions-portfolio-summary">
            <span>
              <strong>{symbols.length}</strong> asset{symbols.length === 1 ? '' : 's'} open
            </span>
            <span>
              Total margin <strong>{fmtUsd(portfolioTotalUsd)}</strong>
            </span>
            {portfolioUpnl != null && (
              <span className={portfolioUpnl >= 0 ? 'pnl-pos' : 'pnl-neg'}>
                Total uPnL <strong>{fmtUsd(portfolioUpnl)}</strong>
              </span>
            )}
          </div>

          {useCompactLotList ? (
            <CompactLotList
              positions={positions}
              maxVisibleLots={maxVisibleLots}
              isRowSelected={isRowSelected}
              busyKey={busyKey}
              onSelectPosition={onSelectPosition}
              onTradeLot={onTradeLot}
              onCloseLot={onCloseLot}
              showLotActions={showLotActions}
              handleCloseLot={handleCloseLot}
            />
          ) : (
          <div className="open-positions-assets">
            {symbols.map((sym) => {
              const assetPositions = netBySymbol[sym] || [];
              const assetFills = fillsBySymbol[sym] || [];
              const tab = assetTabs[sym] || 'summary';
              const symbolTotalUsd = assetPositions.reduce(
                (sum, p) => sum + (Number(lotMarginUsd(p)) || 0),
                0
              );

              return (
                <div key={sym} className="open-positions-asset-panel">
                  <div className="open-positions-asset-header">
                    <h3 className="open-positions-asset-symbol">{sym}</h3>
                    <div
                      className="open-positions-view-toggle open-positions-asset-tabs"
                      role="tablist"
                      aria-label={`${sym} position view`}
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'summary'}
                        className={`open-positions-view-btn ${tab === 'summary' ? 'active' : ''}`}
                        onClick={() => setAssetTab(sym, 'summary')}
                      >
                        Summary
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'trades'}
                        className={`open-positions-view-btn ${tab === 'trades' ? 'active' : ''}`}
                        onClick={() => setAssetTab(sym, 'trades')}
                      >
                        Trades ({assetFills.length})
                      </button>
                    </div>
                  </div>

                  {tab === 'summary' ? (
                    <>
                      <ul className="open-positions-lot-list">
                        {assetPositions.map((p) => {
                          const key = positionKey(p);
                          const isSelected = isRowSelected(p);
                          const rowBusy = busyKey === key;
                          return (
                            <PositionRow
                              key={key}
                              p={p}
                              isSelected={isSelected}
                              busy={rowBusy}
                              onTradeLot={showLotActions ? onTradeLot : onSelectPosition}
                              onCloseLot={showLotActions ? handleCloseLot : undefined}
                            />
                          );
                        })}
                      </ul>
                      <p className="open-positions-asset-total">
                        {sym} total <strong>{fmtUsd(symbolTotalUsd)}</strong>
                      </p>
                    </>
                  ) : assetFills.length ? (
                    <>
                      <AssetFillsTable
                        fills={assetFills}
                        isRowSelected={isRowSelected}
                        onSelectPosition={onSelectPosition}
                      />
                      <p className="open-positions-fills-meta">
                        {assetFills.length} trade{assetFills.length === 1 ? '' : 's'} — first{' '}
                        {Math.min(FILLS_VISIBLE_ROWS, assetFills.length)} visible, scroll for more
                      </p>
                    </>
                  ) : (
                    <p className="open-positions-empty open-positions-asset-empty">
                      No individual fills recorded for {sym}.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          )}
        </>
      )}
    </section>
  );
}
